import { Component, Input, ViewChild, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';

export interface SimpleCarouselConfig {
  slidesPerView?: number;
  slidesPerGroup?: number;
  direction?: 'horizontal' | 'vertical';
}

@Component({
    selector: 'mh-simple-carousel',
    templateUrl: './simple-carousel.component.html',
    styleUrls: ['./simple-carousel.component.scss'],
    standalone: false
})
export class SimpleCarouselComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() config: SimpleCarouselConfig = {
    slidesPerView: 1,
    slidesPerGroup: 1,
    direction: 'horizontal'
  };

  @ViewChild('carouselContainer', { static: false }) carouselContainer!: ElementRef;
  @ViewChild('carouselTrack', { static: false }) carouselTrack!: ElementRef;

  currentIndex = 0;
  canGoNext = false;
  canGoPrev = false;
  private resizeObserver: ResizeObserver | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].firstChange) {
      setTimeout(() => {
        this.updateCarousel();
        this.updateNavigationState();
      });
    }
  }

  ngAfterViewInit(): void {
    // Usar setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.updateCarousel();
      this.updateNavigationState();
      this.cdr.detectChanges();
    });
    
    // Observar cambios en el tamaño del contenedor
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateCarousel();
        this.updateNavigationState();
      });
      this.resizeObserver.observe(this.carouselContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  next(): void {
    const slidesPerGroup = this.config.slidesPerGroup || 4;
    const totalSlides = this.getTotalSlides();
    const slidesPerView = this.config.slidesPerView || 4;
  
    // El índice máximo es el total de slides menos los que ya son visibles.
    const maxIndex = Math.max(0, totalSlides - slidesPerView);
    
    this.currentIndex = Math.min(this.currentIndex + slidesPerGroup, maxIndex);
    this.updateCarousel();
    this.updateNavigationState();
  }
  
  // La función prev() generalmente es correcta, pero la dejamos consistente.
  prev(): void {
    const slidesPerGroup = this.config.slidesPerGroup || 1;
    this.currentIndex = Math.max(0, this.currentIndex - slidesPerGroup);
    this.updateCarousel();
    this.updateNavigationState();
  }

  private getTotalSlides(): number {
    if (!this.carouselTrack) return 0;
    return this.carouselTrack.nativeElement.children.length;
  }

  private updateCarousel(): void {
    // Asegurarse de que los elementos existan
    if (!this.carouselTrack || !this.carouselContainer) return;
  
    const containerElement = this.carouselContainer.nativeElement;
    const trackElement = this.carouselTrack.nativeElement;
    const slides = trackElement.children;
    const totalSlides = slides.length;
  
    // Si no hay slides, no hacer nada
    if (totalSlides === 0) return;
  
    const slidesPerView = this.config.slidesPerView || 1;
    
    // --- 1. CALCULAR Y ASIGNAR EL ANCHO TOTAL DEL TRACK ---
    // Si hay 20 slides y se ven 4, el track debe ser 500% del ancho del contenedor.
    const totalTrackWidthPercent = (totalSlides / slidesPerView) * 100;
    trackElement.style.width = `${totalTrackWidthPercent}%`;
  
    // --- 2. ASIGNAR EL ANCHO A CADA SLIDE INDIVIDUALMENTE ---
    // Cada slide debe ocupar un porcentaje del ancho total del track.
    const slideWidthPercent = 100 / totalSlides;
    for (let i = 0; i < slides.length; i++) {
      const slideElement = slides[i] as HTMLElement;
      // Asignamos el ancho relativo al track
      slideElement.style.width = `${slideWidthPercent}%`;
      slideElement.style.minWidth = `${slideWidthPercent}%`;
      slideElement.style.maxWidth = `${slideWidthPercent}%`;
    }
  
    // --- 3. CALCULAR EL DESPLAZAMIENTO EN PÍXELES (MÁS SEGURO) ---
    const containerWidth = containerElement.offsetWidth;
    const slideWidthInPixels = containerWidth / slidesPerView;
    const offset = -this.currentIndex * slideWidthInPixels;
    trackElement.style.transform = `translateX(${offset}px)`;
  }

  private updateNavigationState(): void {
    const totalSlides = this.getTotalSlides();
    const slidesPerView = this.config.slidesPerView || 1;
    const maxIndex = Math.max(0, totalSlides - slidesPerView);
    
    this.canGoNext = this.currentIndex < maxIndex && totalSlides > slidesPerView;
    this.canGoPrev = this.currentIndex > 0;
  }
}

